import { WORK_SECTION_ORDER, type MyWork, type WorkItem, type WorkSection } from '@/api/work';
import { capitalize } from '@/ui/vocabulary';

import type { SectionPlan } from './planTypes';
import type { RowContext } from './rowText';
import { planFor } from './sectionPlans';

/** The most rows a section shows; the rest are one step away behind "See all". */
export const MAX_ROWS = 5;

/** One thing waiting, worded and linked, ready to draw. */
export interface TodayRow {
  readonly id: string;
  /** Who or what it is about; empty when the item has no name. */
  readonly subject: string;
  /** What is waiting, as a clause with its full stop. */
  readonly predicate: string;
  readonly actionLabel: string;
  readonly to: string;
}

/** The way to a section's whole list: its path, and the name a screen reader gives the link. */
export interface SeeAll {
  readonly to: string;
  readonly label: string;
}

/** A section of work, worded and linked, ready to draw. */
export interface TodaySectionModel {
  readonly key: string;
  readonly title: string;
  /** Every item waiting in the section, not only the rows shown. */
  readonly count: number;
  readonly rows: readonly TodayRow[];
  readonly seeAll: SeeAll | null;
}

const KNOWN_KEYS: readonly string[] = WORK_SECTION_ORDER;

function rankOf(key: string): number {
  const index = KNOWN_KEYS.indexOf(key);
  return index === -1 ? KNOWN_KEYS.length : index;
}

function seeAllFor(plan: SectionPlan, items: readonly WorkItem[]): SeeAll | null {
  const to = plan.seeAllTo(items);
  if (to === null) {
    return null;
  }
  return { to, label: `See all ${plan.title.toLowerCase()}` };
}

function buildSection(section: WorkSection, context: RowContext): TodaySectionModel {
  const plan = planFor(section.key);
  const rows = section.items.slice(0, MAX_ROWS).map((item): TodayRow => {
    const draft = plan.row(item, context);
    const clause = draft.subject === '' ? capitalize(draft.predicate) : draft.predicate;
    return {
      id: `${item.recordType}:${item.recordId}`,
      subject: draft.subject,
      predicate: `${clause}.`,
      actionLabel: draft.action.label,
      to: draft.action.to,
    };
  });
  return {
    key: section.key,
    title: plan.title,
    count: Math.max(section.count, section.items.length),
    rows,
    seeAll: seeAllFor(plan, section.items),
  };
}

/**
 * Turns the server's sections into what Today draws.
 *
 * Sections follow the contract's urgency order whatever order they arrived in, and a section the
 * client has not heard of goes last. Each shows at most {@link MAX_ROWS} rows, one plain sentence
 * and one action apiece. Nothing here reads a clock but the one it is handed.
 *
 * @param work what the server sent
 * @param context the moment and the workspace the sentences are read in
 * @returns the sections to draw
 */
export function buildToday(work: MyWork, context: RowContext): TodaySectionModel[] {
  return work.sections
    .toSorted((a, b) => rankOf(a.key) - rankOf(b.key))
    .map((section) => buildSection(section, context));
}
